import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import earningsService from "./earningsService";

const initialState = {
  dashboard: null,
  history: null,
  allEarnings: null,
  userEarnings: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

export const getEarningsDashboard = createAsyncThunk(
  "earnings/dashboard",
  async ({ page = 1, limit = 20 } = {}, thunkAPI) => {
    try {
      return await earningsService.getEarningsDashboard(page, limit);
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const getEarningsHistory = createAsyncThunk(
  "earnings/history",
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.token;
      return await earningsService.getEarningsHistory(token);
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const getAllEarnings = createAsyncThunk(
  "earnings/getAll",
  async ({ page, limit }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.token;
      return await earningsService.getAllEarnings(token, page, limit);
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const getUserEarnings = createAsyncThunk(
  "earnings/getUser",
  async (userId, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.token;
      return await earningsService.getUserEarnings(token, userId);
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const processPayment = createAsyncThunk(
  "earnings/processPayment",
  async ({ userId, paymentData }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.token;
      return await earningsService.processPayment(token, userId, paymentData);
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const earningsSlice = createSlice({
  name: "earnings",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getEarningsDashboard.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEarningsDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.dashboard = action.payload.data;
      })
      .addCase(getEarningsDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getEarningsHistory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEarningsHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.history = action.payload.data;
      })
      .addCase(getEarningsHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getAllEarnings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllEarnings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.allEarnings = action.payload.data;
      })
      .addCase(getAllEarnings.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getUserEarnings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserEarnings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.userEarnings = action.payload.data;
      })
      .addCase(getUserEarnings.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(processPayment.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(processPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        if (state.userEarnings) {
          state.userEarnings.paymentDetails = action.payload.data;
        }
      })
      .addCase(processPayment.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset } = earningsSlice.actions;
export default earningsSlice.reducer;
